import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import styles from "./LoginForm.module.scss";
import classNames from "classnames/bind";
import { IFormErrors, ILoginPayload } from "~/utils/interface/auth";
import { useState } from "react";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { useAppDispatch } from "~/redux/hooks";
import { handleLogin } from "~/thunks/auth/authThunk";
import { useNavigate } from "react-router-dom";
import { notify } from "~/utils/helper";
import PasswordInput from "~/components/common/password-input/PasswordInput";
import { useTranslation } from "react-i18next";
import { adminRouteAbsolute } from "~/utils/constants/route";
import { useEffect } from "react";
import { StorageEnum } from "~/utils/enum";
const cx = classNames.bind(styles);

const LoginForm = () => {
  const { t } = useTranslation(["admin/auth"]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ILoginPayload>({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<IFormErrors>({});
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const accessToken = localStorage.getItem(StorageEnum.ACCESS_TOKEN);
    if (accessToken) {
      navigate(adminRouteAbsolute.dashboard, { replace: true });
    }
  }, [navigate]);

  // Validate email
  const validateEmail = (email: string): string => {
    if (!email.trim()) {
      return t("email.required");
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t("email.invalid");
    }
    return "";
  };

  // Validate password
  const validatePassword = (password: string): string => {
    if (!password.trim()) {
      return t("password.required");
    }
    return "";
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: IFormErrors = {
      email: validateEmail(formData.email),
      password: validatePassword(formData.password),
    };

    setErrors(newErrors);
    return !newErrors.email && !newErrors.password;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (touched) {
      if (name === "email") {
        const error = validateEmail(value);
        setErrors((prev) => ({ ...prev, email: error }));
      } else if (name === "password") {
        const error = validatePassword(value);
        setErrors((prev) => ({ ...prev, password: error }));
      }
    }
  };

  const handleSubmit = async () => {
    setTouched(true);

    try {
      if (validateForm()) {
        setLoading(true);
        await dispatch(handleLogin(formData)).unwrap();
        navigate(adminRouteAbsolute.dashboard);
      }
    } catch (e) {
      notify(
        "error",
        (e as any).response?.data?.message || t("errorMessage")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleForgotPassword = () => {
    navigate(adminRouteAbsolute.forgotPassword);
  };

  return (
    <div className={cx("login-form-container")}>
      <div className={cx("login-form-content")}>
        <div className={cx("login-label")}>
          <h3 className={cx("title")}>{t("loginForm.title")}</h3>
          <p className={cx("description")}>{t("loginForm.description")}</p>
        </div>

        <form className={cx("login-form")} onSubmit={handleFormSubmit}>
          <PrimaryInput
            name="email"
            label={t("email.label")}
            placeholder={t("email.placeholder")}
            value={formData.email}
            onChange={handleInputChange}
            errorMessage={errors.email}
          />

          <PasswordInput
            name="password"
            label={t("password.label")}
            placeholder={t("password.placeholder")}
            value={formData.password}
            onChange={handleInputChange}
            errorMessage={errors.password}
          />

          <div className={cx("login-form-actions")}>
            <button
              type="button"
              className={cx("forgot-password-button")}
              onClick={handleForgotPassword}
            >
              <p className={cx("forgot-password-text")}>
                {t("forgotPasswordButton")}
              </p>
            </button>

            <PrimaryButton
              title={t("signInButton")}
              className={cx("sign-in-button")}
              type="submit"
              loading={loading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
